import { Component, DestroyRef, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, merge } from 'rxjs';
import { CfiRecordEditComponent } from '@k12/app-components';
import { StudentAwardService } from '@k12/app-components';
import { UserRole } from '@k12/app-components';
import { ESA_SECTIONS, ESA_OPTIONS, publicSchoolDistrictOptions } from '../../student-esa/student-esa.config';
import { computeClassWalletTotals } from '../class-wallet-totals';
import { CwFileTransaction } from '../../../awards/models/cw-file-transaction.model';
import { resolveEsaFundingLevel } from '../esa-funding-level';
import { DisabilityType, FieldOption, HouseholdStudent } from '@k12/models';
import { LoadingSpinnerService } from '@k12/loading';
import { ApplicationStatus, AwardStatus, isAwardStatus, StudentEsa } from '@k12/models';
import { AppInsightsService } from '@k12/app-services';
import { IneligibilityReason } from '../../../awards/models/ineligibilityReason.model';
import { confirmIneligibleTaskCancellation, omitUnchangedEligibility, requireReasonWhenIneligible, withSavedEsa } from '../../application-eligibility.rule';
import { ConfirmDialogService } from '@k12/ui-primitives';
import { FormatDatePipe } from '@k12/pipes';

@Component({
  selector: 'lib-student-esa-detail-edit',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [ReactiveFormsModule, CfiRecordEditComponent],
  template: `
    <cfi-record-edit
      [title]="studentName"
      [sections]="sections"
      [form]="form"
      [options]="options"
      [userRole]="userRole"
      pageTitle="Edit ESA"
      prefix="esa"
      (save)="onSave()"
      (cancelled)="onCancel()" />
  `
})
export class StudentEsaDetailEditComponent implements OnInit {
  public studentInfo: HouseholdStudent | null = null;
  public userRole: UserRole = 'admin';
  public studentId = '';
  public studentName = '';
  public studentEsa!: StudentEsa;
  public academicYearId: number | null = null;

  private readonly fb = inject(FormBuilder);
  private readonly studentAwardService = inject(StudentAwardService);
  private readonly spinnerService = inject(LoadingSpinnerService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly appInsightsService = inject(AppInsightsService);

  readonly sections = ESA_SECTIONS;
  public options: Record<string, FieldOption[]> = {...ESA_OPTIONS};
  readonly form: FormGroup = this.fb.group({
    ...ESA_SECTIONS.flatMap((s) => s.fields).reduce<
      Record<string, [{ value: null; disabled: boolean } | null, ValidatorFn[]]>
    >(
      (acc, field) => ({
        ...acc,
        [field.key]: [field.readonly ? { value: null, disabled: true } : null, field.validators ?? []]
      }),
      {}
    ),
    isAwarded: [false]
  });

  ngOnInit(): void {
    const studentData = this.route.parent?.snapshot.data['student'] ?? this.route.snapshot.data['student'];
    
    this.studentInfo = studentData?.detail as HouseholdStudent | null;
    this.studentEsa = {
      ...studentData?.esa,
      disabilityType1Id: studentData?.esa?.disabilityType1Id ?? '',
      disabilityType2Id: studentData?.esa?.disabilityType2Id ?? ''
    };
    this.studentId = this.route.snapshot.paramMap.get('studentId') ?? '';
    this.academicYearId = this.parseAcademicYearId(this.route.snapshot.queryParamMap.get('academicYearId'));
    this.studentName = this.studentInfo
      ? [this.studentInfo.firstName, this.studentInfo.middleName, this.studentInfo.lastName].filter(Boolean).join(' ')
      : '';
    this.options = {
      ...this.options,
      publicSchoolDistricts: publicSchoolDistrictOptions(this.studentEsa.esaPublicSchoolDistrict),
      disabilityTypes: [
        { label: 'None', value: '' },
        ...(studentData?.disabilityTypes ?? []).map((disability: DisabilityType) => ({
          label: disability.name,
          value: disability.id
        })) ?? []
      ],
      inEligibilityReasons: [
        { label: 'None', value: null },
        ...(studentData?.inEligibilityReasons ?? []).map((reason: IneligibilityReason) => ({
          label: reason.name,
          value: reason.id
        })) ?? []
      ]
    };
    const isAwarded = isAwardStatus(studentData?.esa?.esaAwardStatus, AwardStatus.Accepted, AwardStatus.Awarded);
    this.form.patchValue({
      ...this.studentEsa,
      iepVerifiedDate: FormatDatePipe.toDate(this.studentEsa.iepVerifiedDate),
      esaExitDate: FormatDatePipe.toDate(this.studentEsa.esaExitDate),
      isAwarded
    });
    this.patchClassWalletTotals(studentData);
    this.trackAwardLevel();
    requireReasonWhenIneligible(this.form, 'esaApplicationStatus', 'esaIneligibilityReasonId', this.destroyRef);
  }

  private patchClassWalletTotals(studentData: { cwTransactions?: CwFileTransaction[] | null } | null): void {
    this.form.patchValue(computeClassWalletTotals(studentData?.cwTransactions ?? []));
  }

  /**
   * Award level is display-only — the disabilities decide it, and the API classifies them the same
   * way on save. Resolving it on load as well as on every change means the field always shows the
   * level that will actually be stored, even for a record saved before this rule existed.
   */
  private trackAwardLevel(): void {
    const awardLevel = this.form.get('esaFundingLevel');
    const primaryDisability = this.form.get('disabilityType1Id');
    const secondaryDisability = this.form.get('disabilityType2Id');
    if (!awardLevel || !primaryDisability || !secondaryDisability) {
      return;
    }

    const resolve = () =>
      awardLevel.setValue(resolveEsaFundingLevel(primaryDisability.value, secondaryDisability.value));

    resolve();
    merge(primaryDisability.valueChanges, secondaryDisability.valueChanges)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(resolve);
  }

  onCancel(): void {
    if(this.academicYearId == null) {
      this.router.navigate(['/student', this.studentId], {
        info: {
          skipResolve: true,
          studentData: this.route.parent?.snapshot.data['student']
        }
      });
    }
    else{
      this.router.navigate(['/student', this.studentId], {
        queryParams: {
          academicYearId: this.academicYearId
        },
        info: {
          skipResolve: true,
          studentData: this.route.parent?.snapshot.data['student']
        }
      });
    }
  }

  onSave(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const payload = this.form.getRawValue() as StudentEsa;
      payload.iepVerifiedDate = FormatDatePipe.formatDateToString(this.form.get('iepVerifiedDate')?.value) || null;
      payload.esaExitDate = this.studentEsa?.esaExitDate ?? null;
      payload.disabilityType1Id = payload.disabilityType1Id || null;
      payload.disabilityType2Id = payload.disabilityType2Id || null;
      if (payload.esaApplicationStatus !== ApplicationStatus.Ineligible) {
        payload.esaIneligibilityReasonId = null;
      }

      confirmIneligibleTaskCancellation(this.confirmDialog, this.studentEsa?.esaApplicationStatus, payload.esaApplicationStatus, 'ESA+')
        .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.persist(omitUnchangedEligibility(payload, 'esaApplicationStatus', 'esaIneligibilityReasonId', this.studentEsa)));
    }
  }

  private persist(payload: StudentEsa): void {
    this.spinnerService.showSpinner();
    this.studentAwardService
      .saveEsaStudentDetail(this.studentId, payload, this.academicYearId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.spinnerService.hideSpinner();
          this.studentAwardService
            .getEsa(this.studentId, this.academicYearId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (esa) => {
              if(this.academicYearId == null) {
              this.router.navigate(['/student', this.studentId], {
                info: {
                  skipResolve: true,
                  studentData: withSavedEsa(this.route.parent?.snapshot.data['student'], esa)
                }
              });
            }
            else
            {
              this.router.navigate(['/student', this.studentId], {
                queryParams: {
                  academicYearId: this.academicYearId
                },
                info: {
                  skipResolve: true,
                  studentData: withSavedEsa(this.route.parent?.snapshot.data['student'], esa)
                }
              });
            }
              this.spinnerService.hideSpinner();
              },
              error: (error: unknown) => {
                this.spinnerService.hideSpinner();
                this.appInsightsService.log(error instanceof Error ? error : new Error(String(error)));
              }
          });
        },
        error: () => {
          this.spinnerService.hideSpinner();
        }
      });
  }

  private parseAcademicYearId(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
}
